/* global document */
import * as Msal from "msal";
import { TIMESHEETS_ORDER_BY_SESSION_STORAGE_KEY } from "../components/timesheets/timesheets";

function loggerCallback(logLevel, message) {
  console.log(message);
}

const logger = new Msal.Logger(loggerCallback, {
  level: Msal.LogLevel.Verbose,
  correlationId: "12345"
});

const sharedConfig = {
  auth: {
    clientId: process.env.REACT_APP_AD_CLIENT_ID,
    validateAuthority: false
  },
  cache: {
    cacheLocation: "localStorage"
  },
  system: {
    logger
  }
};

const loginUAAConfig = {
  ...sharedConfig,
  auth: {
    ...sharedConfig.auth,
    authority: process.env.REACT_APP_AD_AUTHORITY,
    redirectUri: `${document.location.protocol}//${document.location.host}/account-portal/callback`
  }
};

const passwordResetUAAConfig = {
  ...sharedConfig,
  auth: {
    ...sharedConfig.auth,
    authority: process.env.REACT_APP_AD_AUTHORITY_RESET,
    redirectUri: `${document.location.protocol}//${document.location.host}/account-portal/reset-callback`
  }
};

const scopes = process.env.REACT_APP_AD_SCOPES
  ? process.env.REACT_APP_AD_SCOPES.split(",")
  : null;

const loginUAAppSingleton = (function loginUAAppSingleton() {
  let instance = null;
  return {
    get: () => {
      if (instance) {
        return instance;
      }
      instance = new Msal.UserAgentApplication(loginUAAConfig);
      return instance;
    }
  };
})();

const passwordResetUAAppSingleton = (function passwordResetUAAppSingleton() {
  let instance = null;
  return {
    get: () => {
      if (instance) {
        return instance;
      }
      instance = new Msal.UserAgentApplication(passwordResetUAAConfig);
      return instance;
    }
  };
})();

// these should only be used within a popup to close it - otherwise use the singletons above
export const createUserAgentApp = () =>
  new Msal.UserAgentApplication(loginUAAConfig);
export const createResetPasswordUAApp = () =>
  new Msal.UserAgentApplication(passwordResetUAAConfig);

const defaultGetTokenOptions = {
  scopes,
  authority: loginUAAConfig.auth.authority
};

export const getTokenSilent = async () =>
  loginUAAppSingleton.get().acquireTokenSilent(defaultGetTokenOptions);

/* returns id_token without any prompt */
const getTokenSilently = async options =>
  loginUAAppSingleton.get().acquireTokenSilent(options);

/* returns id_token through popup window */
const getTokenWithPopup = async options =>
  loginUAAppSingleton.get().acquireTokenPopup(options);

export const getIdToken = async options => {
  const combinedOptions = { ...defaultGetTokenOptions, ...options };
  return new Promise((fulfil, reject) => {
    getTokenSilently(combinedOptions)
      .then(user => fulfil(user.idToken.rawIdToken))
      .catch(() => getTokenWithPopup(combinedOptions))
      .then(user => fulfil(user.idToken.rawIdToken))
      .catch(err => reject(err));
  });
};

/* prompts to login/signup - returns *id_token* when complete */
const promptLogin = async (options = defaultGetTokenOptions) =>
  loginUAAppSingleton.get().loginPopup(options);

/* tries 3 methods for getting an id token (in series) - resolving the first successful call,
 * or rejecting if all the methods fail
 */
export const getToken = async (options = {}) => {
  const combinedOptions = { ...defaultGetTokenOptions, ...options };
  return new Promise((fulfil, reject) => {
    getIdToken(combinedOptions)
      .then(user => fulfil(user.idToken.rawIdToken))
      .catch(() => promptLogin(combinedOptions))
      .then(user => fulfil(user.idToken.rawIdToken))
      .catch(err => reject(err));
  });
};

export const startPasswordReset = async () =>
  passwordResetUAAppSingleton.get().acquireTokenPopup({
    scopes,
    authority: passwordResetUAAConfig.auth.authority
  });

export const clearStorage = () => {
  sessionStorage.removeItem(TIMESHEETS_ORDER_BY_SESSION_STORAGE_KEY);
};

export const logout = async () => {
  clearStorage();
  return Promise.all(
    [loginUAAppSingleton.get(), passwordResetUAAppSingleton.get()]
      .filter(app => app.getAccount() !== null)
      .map(app => app.logout())
  );
};
